import axios from 'axios';
import { API_URL } from '../config/const';
import { getAuthHeaders } from './AuthHeader/getAuthHeader';
import { fetchAuthSession, signOut } from '@aws-amplify/auth';
import toast from 'react-hot-toast';
import { clearAllAuthStorage, getSuppressAuthToasts, isRevokedRefreshTokenError } from '../utils/authStorage';
import { extractApiError, isPresignedUrlExpired } from '../utils/util';

/**
 * Central API client for all backend requests.
 * Use this (default export) for every fetch/axios call to the API so that:
 * - 4xx/5xx and network errors are caught in one place and trigger react-hot-toast notifications.
 * - 401 triggers token refresh and retry, or redirect to login.
 */
const api = axios.create({
    baseURL: API_URL,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor: centralised error handling – all 4xx/5xx (and network errors) trigger a toast
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const suppressToast = Boolean((error as any)?.config?.suppressToast);
        // Don't show toast for cancelled requests (e.g. on URL change / navigation)
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }
        if (error.response) {
            const status = error.response.status;
            switch (status) {
                case 401: {
                    const originalConfig: any = error.config || {};
                    originalConfig._retryCount = originalConfig._retryCount || 0;

                    // If we've already retried once, clear auth and redirect instead of looping
                    if (originalConfig._retryCount >= 1) {
                        clearAllAuthStorage();
                        const pathname = window.location.pathname || '';
                        const onLoginPage = pathname === '/login' || pathname.endsWith('/login');
                        if (!onLoginPage) {
                            window.location.href = '/login';
                        }
                        break;
                    }

                    try {
                        await fetchAuthSession({ forceRefresh: true });
                        const authHeaders = await getAuthHeaders();
                        originalConfig.headers = { ...originalConfig.headers, ...authHeaders };
                        originalConfig._retryCount += 1;
                        return api.request(originalConfig);
                    } catch (refreshError) {
                        try {
                            await signOut();
                        } catch {
                            // ignore signOut errors
                        }
                        clearAllAuthStorage();
                        const pathname = window.location.pathname || '';
                        const onLoginPage = pathname === '/login' || pathname.endsWith('/login');
                        if (!onLoginPage) {
                            if (!getSuppressAuthToasts() && isRevokedRefreshTokenError(refreshError)) {
                                toast.error('Your session has expired. Please sign in again.');
                            }
                            window.location.href = '/login';
                        }
                    }
                    break;
                }
                case 403:
                    {
                        if (suppressToast) break;
                        const url = typeof error?.config?.url === 'string' ? error.config.url : '';
                        // If a direct S3 presigned request fails with 403 and it is expired, guide the user.
                        if (url && isPresignedUrlExpired(url)) {
                            toast.error('Link expired. Please refresh and try again.');
                        } else {
                            toast.error('Access denied');
                        }
                    }
                    break;
                case 500:
                    if (!suppressToast) toast.error('Something went wrong');
                    break;
                default:
                    if (!suppressToast) toast.error(extractApiError(error));
            }
        } else {
            // No response (network error, timeout, etc.)
            if (!suppressToast) toast.error(extractApiError(error));
        }

        return Promise.reject(error);
    }
);

export default api;


export const getHeaders = async () => {
    const authHeaders = await getAuthHeaders();
    return {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
        }
    }
}